import React, { useState, useMemo, useEffect } from 'react';
import {
  X, Sparkles, Info, Wand2, ChevronDown,
  Plus, GripVertical, Zap,
} from 'lucide-react';
import { WS, SKILL_LIBRARY, SKILL_DOMAINS, getSuggestedSkills, type SkillItem } from './workspaceData.ts';
import SkillCard, { SKILL_ICONS } from './SkillCard.tsx';
import SkillTemplatesModal from './SkillTemplatesModal.tsx';
import { Package } from 'lucide-react';
import PromptIDE from './PromptIDE.tsx';
import { userAPI } from '../../services/api.js';
import { AGENT_DOMAINS } from '../../config/agentDomains.ts';
import { alpha } from '../../utils/alpha';
import { useSkillCatalogue } from '../../hooks/useSkillCatalogue.ts';

interface AgentWorkspaceCenterProps {
  // Agent Identity
  name: string; setName: (v: string) => void;
  isLyran?: boolean;
  description: string; setDescription: (v: string) => void;
  domain: string; setDomain: (v: string) => void;
  personality: string; setPersonality: (v: string) => void;
  goal: string; setGoal: (v: string) => void;
  inputHint: string; setInputHint: (v: string) => void;
  requiredInputType: string; setRequiredInputType: (v: string) => void;

  // System Prompt
  systemPrompt: string; setSystemPrompt: (v: string) => void;

  // Skills
  activeSkillIds: string[];
  onToggleSkill: (id: string) => void;
  onRemoveSkill: (id: string) => void;
  externalNameConflict?: string;
  onNameEdit?: () => void;

  // Skips the internal "Agents / Create a new agent" page header — for use
  // when this component is embedded inside a page that already renders its
  // own title (e.g. CreateAgentPage's Advanced tab), so the two don't stack.
  hideHeader?: boolean;
}

// ─── Active Skill Pill ────────────────────────────────────────────────────────
function SkillPill({ skill, onRemove }: { skill: SkillItem; onRemove: () => void }) {
  const [hovered, setHovered] = useState(false);
  const domain = SKILL_DOMAINS.find(d => d.id === skill.domain);
  const color = domain?.color || WS.accent;

  return (
    <div
      className="ws-pill-animate"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        borderRadius: 12,
        background: hovered ? `${alpha(color, '1A')}` : `${alpha(color, '10')}`,
        border: `1px solid ${hovered ? color : `${alpha(color, '30')}`}`,
        cursor: 'grab',
        userSelect: 'none',
        transition: 'transform 0.16s ease, box-shadow 0.16s ease, background 0.16s',
        transform: hovered ? 'translateY(-3px)' : 'none',
        boxShadow: hovered ? WS.accentGlow : 'none',
        flexShrink: 0,
      }}
    >
      {/* status dot */}
      <div style={{ width: 8, height: 8, borderRadius: 99, background: skill.tags && skill.tags.includes('live') ? WS.green : WS.ink4, boxShadow: skill.tags && skill.tags.includes('live') ? '0 0 8px rgba(22,163,74,0.12)' : 'none' }} />
      <GripVertical size={10} color={`${alpha(color, '60')}`} style={{ cursor: 'grab' }} />
      {(() => { const Icon = SKILL_ICONS[skill.icon] || Package; return <Icon size={12} color={color} strokeWidth={1.75} />; })()}
      <span style={{ fontSize: 11.5, fontWeight: 500, color, whiteSpace: 'nowrap' }}>
        {skill.name}
      </span>
      <button
        onClick={e => { e.stopPropagation(); onRemove(); }}
        style={{
          width: 14, height: 14,
          borderRadius: '50%',
          background: hovered ? `${alpha(color, '30')}` : 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
          transition: 'background 0.12s',
        }}
      >
        <X size={9} color={color} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ─── Input / Textarea helpers ─────────────────────────────────────────────────
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11.5, fontWeight: 500, color: WS.ink3,
  display: 'block', marginBottom: 5,
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--lyra-color-surface)',
  border: `1px solid ${WS.border}`,
  borderRadius: 9,
  padding: '8px 12px',
  fontSize: 13,
  color: WS.ink,
  fontFamily: 'inherit',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AgentWorkspaceCenter({
  name, setName,
  isLyran = false,
  description, setDescription,
  domain, setDomain,
  personality, setPersonality,
  goal, setGoal,
  inputHint, setInputHint,
  requiredInputType, setRequiredInputType,
  systemPrompt, setSystemPrompt,
  activeSkillIds, onToggleSkill, onRemoveSkill,
  externalNameConflict,
  onNameEdit,
  hideHeader = false,
}: AgentWorkspaceCenterProps) {
  const { skills: catalogueSkills } = useSkillCatalogue();
  const allSkills = catalogueSkills.length ? catalogueSkills : SKILL_LIBRARY;

  const [showTooltip, setShowTooltip] = useState(false);
  const [improveHovered, setImproveHovered] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [nameTaken, setNameTaken] = useState(false);

  // Sections 1 & 2 are collapsible (like section 4 already is) so the page
  // isn't a permanently-tall wall of open forms — users can shrink a
  // section once they're done with it. Basic info starts open (every
  // agent needs it); Skills starts open only if it already has content
  // (existing agent) or collapsed for a fresh one, matching the same
  // "collapsed until there's something to show" default used elsewhere
  // in the builder (Skill Library sidebar, Advanced runtime settings).
  const [basicOpen, setBasicOpen] = useState(true);
  const [skillsOpen, setSkillsOpen] = useState(activeSkillIds.length > 0);
  const [skillTab, setSkillTab] = useState<'library' | 'active'>('library');
  const [skillSearch, setSkillSearch] = useState('');
  const [browseModalOpen, setBrowseModalOpen] = useState(false);
  const [templateSkill, setTemplateSkill] = useState<SkillItem | null>(null);

  const activeSkills: SkillItem[] = useMemo(() =>
    activeSkillIds
      .map(id => allSkills.find(s => s.id === id))
      .filter(Boolean) as SkillItem[],
    [activeSkillIds, allSkills]
  );

  const suggestedSkills: SkillItem[] = useMemo(() =>
    getSuggestedSkills(systemPrompt).filter(s => !activeSkillIds.includes(s.id)),
    [systemPrompt, activeSkillIds]
  );

  // Inline "Skill library" tab results — a quick-search of the whole
  // catalogue without leaving the page. Empty query falls back to the most
  // popular skills so the tab is never blank; full browsing (grouped by
  // domain) lives in the "Browse library" modal below.
  const skillMatches = (s: SkillItem, q: string) =>
    `${s.name} ${s.description} ${s.tags.join(' ')}`.toLowerCase().includes(q.toLowerCase());
  const libraryResults: SkillItem[] = useMemo(() => {
    const pool = allSkills.filter(s => !activeSkillIds.includes(s.id));
    const q = skillSearch.trim();
    if (!q) return [...pool].sort((a, b) => Number(b.uses) - Number(a.uses)).slice(0, 8);
    return pool.filter(s => skillMatches(s, q)).slice(0, 24);
  }, [skillSearch, activeSkillIds, allSkills]);

  // Browse-library modal: full catalogue, grouped by domain, own search box.
  const [modalSearch, setModalSearch] = useState('');
  const groupedModalResults = useMemo(() => {
    const q = modalSearch.trim();
    const pool = q ? allSkills.filter(s => skillMatches(s, q)) : allSkills;
    return SKILL_DOMAINS
      .map(d => ({ domain: d, skills: pool.filter(s => s.domain === d.id) }))
      .filter(g => g.skills.length > 0);
  }, [modalSearch, allSkills]);

  const charCount = systemPrompt.length;
  const tokenEst = Math.ceil(charCount / 4);
  const lineCount = systemPrompt.split('\n').length;
  const skillPromptInjections = activeSkills.filter(s => s.prompt);

  return (
    <>
    <div
      className="ws-center-root"
      style={{
        height: '100%',
        overflowY: 'auto',
        background: WS.bg,
      }}
    >
      <style>{`
        .awc-page {
          width: 100%;
          max-width: 980px;
          margin: 0 auto;
          padding: 28px 30px 56px;
          box-sizing: border-box;
        }

        .awc-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 24px;
          margin-bottom: 22px;
        }

        .awc-eyebrow {
          font-size: 11px;
          font-weight: 700;
          color: #8792A3;
          text-transform: uppercase;
          letter-spacing: .08em;
          margin-bottom: 7px;
        }

        .awc-title {
          font-size: 24px;
          line-height: 1.2;
          font-weight: 750;
          letter-spacing: -.025em;
          color: var(--lyra-color-text);
          margin: 0;
        }

        .awc-subtitle {
          margin: 6px 0 0;
          color: #7C8798;
          font-size: 13px;
          line-height: 1.5;
          max-width: 620px;
        }

        .awc-section {
          background: var(--lyra-color-surface);
          border: 1px solid var(--app-surface-3);
          border-radius: 14px;
          margin-bottom: 12px;
          overflow: hidden;
        }

        .awc-section-head {
          display: flex;
          align-items: center;
          gap: 11px;
          padding: 15px 18px 12px;
        }

        .awc-number {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          background: #177057;
          color: var(--app-on-accent);
          font-size: 12px;
          font-weight: 750;
        }

        .awc-section-title {
          font-size: 14px;
          line-height: 1.25;
          font-weight: 700;
          color: var(--lyra-color-text);
        }

        .awc-section-help {
          font-size: 11px;
          color: var(--app-text-3);
          margin-top: 2px;
        }

        .awc-section-body {
          padding: 0 18px 18px;
        }

        .awc-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .awc-grid-3 {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 14px;
        }

        .awc-field {
          min-width: 0;
        }

        .awc-label {
          display: block;
          font-size: 11px;
          font-weight: 650;
          color: var(--lyra-color-text);
          margin-bottom: 6px;
        }

        .awc-input,
        .awc-textarea,
        .awc-select {
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #DCE1E7;
          background: var(--lyra-color-surface);
          color: var(--lyra-color-text);
          border-radius: 8px;
          font: inherit;
          font-size: 12.5px;
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }

        .awc-input,
        .awc-select {
          height: 38px;
          padding: 0 11px;
        }

        .awc-textarea {
          min-height: 72px;
          padding: 9px 11px;
          resize: vertical;
          line-height: 1.45;
        }

        .awc-input:focus,
        .awc-textarea:focus,
        .awc-select:focus {
          border-color: #8BCDBB;
          box-shadow: 0 0 0 3px rgba(23,112,87,.08);
        }

        .awc-name-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .awc-conflict {
          flex: 0 0 auto;
          background: var(--lyra-color-surface-2);
          color: #B42318;
          border: 1px solid var(--app-danger-border);
          border-radius: 999px;
          padding: 5px 9px;
          font-size: 11px;
          font-weight: 650;
          white-space: nowrap;
        }

        .awc-skill-tabs {
          display: flex;
          align-items: center;
          gap: 18px;
          border-bottom: 1px solid var(--app-surface-3);
          margin-bottom: 12px;
        }

        .awc-skill-tab {
          border: 0;
          background: transparent;
          padding: 0 0 9px;
          font: inherit;
          font-size: 11.5px;
          color: #7C8798;
          cursor: pointer;
        }
        .awc-skill-tab:hover:not(.active) {
          color: #4B5563;
        }

        .awc-skill-tab.active {
          color: #177057;
          font-weight: 700;
          border-bottom: 2px solid #177057;
        }

        .awc-skill-search {
          display: flex;
          gap: 8px;
          margin-bottom: 10px;
        }

        .awc-search-box {
          flex: 1;
          min-width: 0;
          height: 36px;
          border: 1px solid #DCE1E7;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 10px;
          color: #9AA4B2;
          font-size: 12px;
        }

        .awc-browse {
          height: 36px;
          border: 1px solid #B8E1D4;
          background: var(--lyra-color-surface-2);
          color: #17644F;
          border-radius: 8px;
          padding: 0 12px;
          font: inherit;
          font-size: 11.5px;
          font-weight: 650;
          white-space: nowrap;
        }

        .awc-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .awc-empty-skills {
          border: 1px dashed #D9DEE5;
          border-radius: 9px;
          min-height: 52px;
          display: flex;
          align-items: center;
          padding: 8px 10px;
          color: #98A2B1;
          font-size: 11.5px;
        }

        .awc-exec {
          margin-top: 12px;
          padding: 9px 11px;
          border: 1px solid var(--app-surface-3);
          background: var(--lyra-color-surface-2);
          border-radius: 9px;
          display: flex;
          align-items: center;
          gap: 10px;
          overflow: hidden;
        }

        .awc-exec-label {
          flex: 0 0 auto;
          font-size: 10px;
          font-weight: 700;
          color: var(--app-text-3);
          text-transform: uppercase;
          letter-spacing: .07em;
        }

        .awc-exec-scroll {
          display: flex;
          align-items: center;
          gap: 6px;
          overflow-x: auto;
          min-width: 0;
        }

        .awc-exec-item {
          flex: 0 0 auto;
          border: 1px solid var(--app-surface-3);
          background: var(--lyra-color-surface);
          border-radius: 7px;
          padding: 6px 9px;
          color: #344054;
          font-size: 11px;
        }

        .awc-prompt {
          border: 1px solid #DCE1E7;
          border-radius: 10px;
          overflow: hidden;
          background: var(--lyra-color-surface);
        }

        .awc-prompt-toolbar {
          min-height: 42px;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 11px;
          border-bottom: 1px solid var(--app-surface-3);
          background: var(--lyra-color-surface-2);
        }

        .awc-prompt-title {
          flex: 1;
          font-size: 11px;
          font-weight: 700;
          color: #354052;
        }

        .awc-token-chip {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 4px 8px;
          border: 1px solid var(--app-surface-3);
          border-radius: 999px;
          background: var(--lyra-color-surface);
          font-size: 9.5px;
          color: #8993A2;
          font-family: "JetBrains Mono", monospace;
          white-space: nowrap;
        }

        .awc-tool-btn {
          height: 29px;
          padding: 0 9px;
          border-radius: 7px;
          background: var(--lyra-color-surface);
          border: 1px solid #DCE1E7;
          color: #596579;
          font: inherit;
          font-size: 10.5px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
        }

        .awc-tool-btn:hover {
          border-color: #B7D9CF;
          color: #17644F;
          background: var(--lyra-color-surface-2);
        }

        .awc-injections {
          border-top: 1px dashed #DCE1E7;
          padding: 12px 14px 14px;
          background: var(--lyra-color-surface-2);
        }

        .awc-injection-title {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
          color: #17644F;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .07em;
        }

        .awc-injection {
          margin-top: 8px;
        }

        .awc-injection-name {
          font-size: 10.5px;
          font-weight: 650;
          color: #667085;
          margin-bottom: 3px;
        }

        .awc-injection-code {
          padding: 7px 9px;
          border: 1px solid var(--app-surface-3);
          border-radius: 7px;
          background: var(--lyra-color-surface);
          color: #7A8493;
          font-size: 10.5px;
          line-height: 1.5;
          font-family: "JetBrains Mono", monospace;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .awc-suggestions {
          margin-top: 12px;
          padding: 0 2px 4px;
        }

        .awc-suggestions-head {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-bottom: 9px;
        }

        .awc-suggestions-title {
          font-size: 11px;
          font-weight: 700;
          color: #556174;
          text-transform: uppercase;
          letter-spacing: .07em;
        }

        .awc-suggestions-help {
          color: #98A2B1;
          font-size: 10.5px;
        }

        .awc-suggestion-scroll {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 3px;
        }

        .awc-suggestion-card {
          flex: 0 0 200px;
        }

        .awc-collapse {
          width: 100%;
          border: 0;
          background: transparent;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          cursor: pointer;
          font: inherit;
          text-align: left;
        }

        .awc-collapse:hover {
          background: var(--lyra-color-surface-2);
        }

        .awc-collapse-main {
          flex: 1;
        }

        .awc-collapse-title {
          font-size: 13px;
          font-weight: 700;
          color: var(--lyra-color-text);
        }

        .awc-collapse-help {
          font-size: 10.5px;
          color: #98A2B1;
          margin-top: 2px;
        }

        .awc-identity {
          padding: 4px 18px 18px;
          border-top: 1px solid var(--app-surface-3);
        }

        @media (max-width: 760px) {
          .awc-page {
            padding: 20px 14px 38px;
          }

          .awc-header {
            margin-bottom: 16px;
          }

          .awc-title {
            font-size: 21px;
          }

          .awc-subtitle {
            font-size: 12px;
          }

          .awc-grid-2,
          .awc-grid-3 {
            grid-template-columns: 1fr;
          }

          .awc-section-head {
            padding: 13px 14px 10px;
          }

          .awc-section-body {
            padding: 0 14px 14px;
          }

          .awc-number {
            width: 26px;
            height: 26px;
          }

          .awc-name-wrap {
            align-items: flex-start;
            flex-direction: column;
          }

          .awc-conflict {
            white-space: normal;
          }

          .awc-prompt-toolbar {
            flex-wrap: wrap;
            padding: 8px 10px;
          }

          .awc-token-chip {
            order: 3;
            width: 100%;
            justify-content: center;
          }

          .awc-tool-btn {
            height: 31px;
          }

          .awc-skill-search {
            flex-direction: column;
          }

          .awc-browse {
            width: 100%;
          }

          .awc-exec {
            align-items: flex-start;
            flex-direction: column;
          }
        }
      `}</style>

      <div className="awc-page">

        {/* Header — omitted when embedded in a page that already has its
            own title (see hideHeader above). */}
        {!hideHeader && (
          <header className="awc-header">
            <div>
              <div className="awc-eyebrow">Agents</div>
              <h1 className="awc-title">{name?.trim() ? name : 'Create a new agent'}</h1>
              <p className="awc-subtitle">
                Design your agent, give it the right skills and instructions, and let Lyra do the rest.
              </p>
            </div>
          </header>
        )}

        {/* 1. Basic information */}
        <section className="awc-section">
          <button
            type="button"
            className="awc-collapse"
            onClick={() => setBasicOpen(v => !v)}
          >
            <div className="awc-number">1</div>
            <div className="awc-collapse-main">
              <div className="awc-collapse-title">Basic information</div>
              <div className="awc-collapse-help">
                {basicOpen || !name.trim() ? 'Give your agent a clear identity and purpose.' : name}
              </div>
            </div>
            <ChevronDown
              size={15}
              color="var(--app-text-3)"
              style={{
                transform: basicOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform .2s',
              }}
            />
          </button>

          {basicOpen && (
          <div className="awc-section-body">
            <div className="awc-grid-2">
              <div className="awc-field">
                <label className="awc-label">Agent name</label>
                <div className="awc-name-wrap">
                  <input
                    className="awc-input"
                    value={name}
                    onChange={e => {
                      setName(e.target.value);
                      if (nameTaken) setNameTaken(false);
                      onNameEdit?.();
                    }}
                    onBlur={async e => {
                      try {
                        const val = (e.target as HTMLInputElement).value.trim();
                        if (!val) return;
                        const resp = await userAPI.checkLyranName(val);
                        if (resp?.exists) {
                          setNameTaken(!resp.ownerIsCurrent);
                        } else {
                          setNameTaken(false);
                        }
                      } catch {
                        // Preserve existing behaviour: availability check failures are UI-only.
                      }
                    }}
                    placeholder="e.g. Market Research Analyst"
                  />
                  {(nameTaken || externalNameConflict) && (
                    <div className="awc-conflict">
                      {externalNameConflict || 'Duplicate Lyran! not allowed..'}
                    </div>
                  )}
                </div>
              </div>

              <div className="awc-field">
                <label className="awc-label">Description</label>
                <textarea
                  className="awc-textarea"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe what this agent does, its goals and when to use it..."
                />
              </div>

              <div className="awc-field">
                <label className="awc-label">Domain</label>
                <div style={{ position: 'relative' }}>
                  <select
                    className="awc-select"
                    value={domain || 'general'}
                    onChange={e => setDomain(e.target.value)}
                    style={{ appearance: 'none', paddingRight: 30, cursor: 'pointer' }}
                  >
                    {AGENT_DOMAINS.map(d => (
                      <option key={d.id} value={d.id}>{d.label}</option>
                    ))}
                  </select>
                  <ChevronDown
                    size={13}
                    color="var(--app-text-3)"
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  />
                </div>
                <div style={{ fontSize: 10.5, color: '#98A2B1', marginTop: 4 }}>
                  Groups this agent on the Agents page and as a quick-pick on New Task.
                </div>
              </div>
            </div>
          </div>
          )}
        </section>

        {/* 2. Skills */}
        <section className="awc-section">
          <button
            type="button"
            className="awc-collapse"
            onClick={() => setSkillsOpen(v => !v)}
          >
            <div className="awc-number">2</div>
            <div className="awc-collapse-main">
              <div className="awc-collapse-title">Skills</div>
              <div className="awc-collapse-help">
                {skillsOpen || activeSkills.length === 0
                  ? 'Add skills from the library or use your active skills below.'
                  : `${activeSkills.length} skill${activeSkills.length > 1 ? 's' : ''} added`}
              </div>
            </div>
            <ChevronDown
              size={15}
              color="var(--app-text-3)"
              style={{
                transform: skillsOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform .2s',
              }}
            />
          </button>

          {skillsOpen && (
          <div className="awc-section-body">
            <div className="awc-skill-tabs">
              <button
                type="button"
                className={`awc-skill-tab ${skillTab === 'library' ? 'active' : ''}`}
                onClick={() => setSkillTab('library')}
              >
                Skill library
              </button>
              <button
                type="button"
                className={`awc-skill-tab ${skillTab === 'active' ? 'active' : ''}`}
                onClick={() => setSkillTab('active')}
              >
                Active skills {activeSkills.length ? `(${activeSkills.length})` : ''}
              </button>
            </div>

            {skillTab === 'library' && (
              <>
                <div className="awc-skill-search">
                  <div className="awc-search-box">
                    <Sparkles size={14} />
                    <input
                      value={skillSearch}
                      onChange={e => setSkillSearch(e.target.value)}
                      placeholder="Search for skills, tools or capabilities..."
                      style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', font: 'inherit', fontSize: 12, color: WS.ink }}
                    />
                  </div>
                  <button className="awc-browse" type="button" onClick={() => setBrowseModalOpen(true)}>
                    <Plus size={12} /> Browse library
                  </button>
                </div>

                {libraryResults.length === 0 ? (
                  <div className="awc-empty-skills">
                    No skills match "{skillSearch}" — try{' '}
                    <button type="button" onClick={() => setBrowseModalOpen(true)} style={{ border: 'none', background: 'none', color: WS.accent, fontWeight: 600, cursor: 'pointer', font: 'inherit', padding: 0 }}>
                      browsing the full library
                    </button>.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                    {libraryResults.map(skill => (
                      <SkillCard key={skill.id} skill={skill} isActive={false} onToggle={onToggleSkill} compact />
                    ))}
                  </div>
                )}
              </>
            )}

            {skillTab === 'active' && (
            activeSkills.length === 0 ? (
              <div className="awc-empty-skills">
                No active skills yet —{' '}
                <button type="button" onClick={() => setBrowseModalOpen(true)} style={{ border: 'none', background: 'none', color: WS.accent, fontWeight: 600, cursor: 'pointer', font: 'inherit', padding: 0 }}>
                  add some from the library
                </button>, or use a slash command in the prompt.
              </div>
            ) : (
              <div className="awc-chip-row">
                {activeSkills.map(skill => (
                  <SkillPill
                    key={skill.id}
                    skill={skill}
                    onRemove={() => onRemoveSkill(skill.id)}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setBrowseModalOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '5px 10px',
                    borderRadius: 999,
                    border: `1px dashed ${WS.border3}`,
                    background: 'transparent',
                    color: WS.ink4,
                    fontSize: 11,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <Plus size={10} /> Add skill
                </button>
              </div>
            ))}

            {activeSkills.length > 0 && (
              <div className="awc-exec">
                <div className="awc-exec-label">Execution</div>
                <div className="awc-exec-scroll">
                  {activeSkills.slice(0, 6).map((s, i) => (
                    <React.Fragment key={s.id}>
                      <div className="awc-exec-item">{s.name}</div>
                      {i < Math.min(activeSkills.length - 1, 5) && (
                        <span style={{ color: '#AAB2BE', fontSize: 11 }}>→</span>
                      )}
                    </React.Fragment>
                  ))}
                  {activeSkills.length > 6 && (
                    <span style={{ fontSize: 10.5, color: '#98A2B1' }}>
                      +{activeSkills.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </section>

        {/* 3. Instructions */}
        <section className="awc-section">
          <div className="awc-section-head">
            <div className="awc-number">3</div>
            <div>
              <div className="awc-section-title">Instructions</div>
              <div className="awc-section-help">Define how your agent should think, respond and operate.</div>
            </div>
          </div>

          <div className="awc-section-body">
            <div className="awc-prompt">
              <div className="awc-prompt-toolbar">
                <span className="awc-prompt-title">System prompt</span>

                <div
                  style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <Info size={13} color="var(--app-text-3)" />
                  {showTooltip && (
                    <div style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 7px)',
                      right: 0,
                      zIndex: 50,
                      width: 260,
                      background: 'var(--lyra-color-text)',
                      color: 'var(--app-on-accent)',
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontSize: 10.5,
                      lineHeight: 1.5,
                      boxShadow: '0 8px 24px rgba(17,24,39,.18)',
                    }}>
                      The system prompt defines your agent's persona, capabilities and constraints.
                    </div>
                  )}
                </div>

                <div className="awc-token-chip">
                  <span>{charCount.toLocaleString()} chars</span>
                  <span>·</span>
                  <span style={{ color: '#177057' }}>~{tokenEst.toLocaleString()} tokens</span>
                  <span>·</span>
                  <span>{lineCount} lines</span>
                </div>

                <button
                  type="button"
                  className="awc-tool-btn"
                  onMouseEnter={() => setImproveHovered(true)}
                  onMouseLeave={() => setImproveHovered(false)}
                  style={improveHovered ? { borderColor: '#B7D9CF', color: '#17644F' } : undefined}
                  onClick={() => {}}
                >
                  <Wand2 size={12} /> Improve
                </button>

                {charCount > 0 && (
                  <button
                    type="button"
                    className="awc-tool-btn"
                    onClick={() => setSystemPrompt('')}
                  >
                    Clear
                  </button>
                )}
              </div>

              <PromptIDE
                systemPrompt={systemPrompt}
                setSystemPrompt={setSystemPrompt}
                skillPromptInjections={skillPromptInjections}
              />

              {skillPromptInjections.length > 0 && (
                <div className="awc-injections">
                  <div className="awc-injection-title">
                    <Zap size={11} />
                    Skill instructions · appended at runtime
                  </div>
                  {skillPromptInjections.map(skill => (
                    <div className="awc-injection" key={skill.id}>
                      <div className="awc-injection-name" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <span>{skill.name}</span>
                        {(skill.templateCount || 0) > 0 && (
                          <button
                            type="button"
                            onClick={() => setTemplateSkill(skill)}
                            style={{ border: `1px solid ${WS.border2}`, background: 'var(--lyra-color-surface)', borderRadius: 8, padding: '3px 10px', fontSize: 11.5, fontWeight: 600, color: WS.ink2, cursor: 'pointer' }}
                          >
                            Templates ({skill.templateCount})
                          </button>
                        )}
                      </div>
                      <div className="awc-injection-code">{skill.prompt}</div>
                    </div>
                  ))}
                </div>
              )}
              {templateSkill && (
                <SkillTemplatesModal
                  skill={templateSkill}
                  onClose={() => setTemplateSkill(null)}
                  onApply={(text, template) => {
                    setSystemPrompt(`${systemPrompt ? systemPrompt.trimEnd() + '\n\n' : ''}### ${templateSkill.name} — ${template.name}\n${text}`);
                    setTemplateSkill(null);
                  }}
                />
              )}
            </div>
          </div>
        </section>

        {/* Suggested skills remain available, but visually subordinate. */}
        {suggestedSkills.length > 0 && (
          <div className="awc-suggestions">
            <div className="awc-suggestions-head">
              <Sparkles size={13} color="#177057" />
              <span className="awc-suggestions-title">Suggested skills</span>
              <span className="awc-suggestions-help">Based on your prompt</span>
            </div>
            <div className="awc-suggestion-scroll">
              {suggestedSkills.map(skill => (
                <div className="awc-suggestion-card" key={skill.id}>
                  <SkillCard
                    skill={skill}
                    isActive={activeSkillIds.includes(skill.id)}
                    onToggle={onToggleSkill}
                    animate
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. Identity & input configuration */}
        <section className="awc-section">
          <button
            type="button"
            className="awc-collapse"
            onClick={() => setIdentityOpen(v => !v)}
          >
            <div className="awc-number">4</div>
            <div className="awc-collapse-main">
              <div className="awc-collapse-title">Identity & input configuration</div>
              <div className="awc-collapse-help">Personality, goal, input hints and required input type.</div>
            </div>
            <ChevronDown
              size={15}
              color="var(--app-text-3)"
              style={{
                transform: identityOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform .2s',
              }}
            />
          </button>

          {identityOpen && (
            <div className="awc-identity">
              <div className="awc-grid-2">
                <div className="awc-field">
                  <label className="awc-label">Personality / Tone</label>
                  <input
                    className="awc-input"
                    value={personality}
                    onChange={e => setPersonality(e.target.value)}
                    placeholder="concise and professional"
                  />
                </div>

                <div className="awc-field">
                  <label className="awc-label">Primary Goal</label>
                  <input
                    className="awc-input"
                    value={goal}
                    onChange={e => setGoal(e.target.value)}
                    placeholder="e.g. synthesise research reports"
                  />
                </div>

                <div className="awc-field">
                  <label className="awc-label">Input Hint</label>
                  <input
                    className="awc-input"
                    value={inputHint}
                    onChange={e => setInputHint(e.target.value)}
                    placeholder="e.g. Attach a .xlsx file to begin"
                  />
                </div>

                <div className="awc-field">
                  <label className="awc-label">Required Input Type</label>
                  <div style={{ position: 'relative' }}>
                    <select
                      className="awc-select"
                      value={requiredInputType}
                      onChange={e => setRequiredInputType(e.target.value)}
                      style={{ appearance: 'none', paddingRight: 30, cursor: 'pointer' }}
                    >
                      <option value="">None (text prompt only)</option>
                      <option value="text">Text</option>
                      <option value="file">File (required)</option>
                      <option value="file+text">File + Text</option>
                      <option value="url">URL</option>
                    </select>
                    <ChevronDown
                      size={13}
                      color="var(--app-text-3)"
                      style={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        <div style={{
          marginTop: 14,
          padding: '10px 2px 0',
          color: '#98A2B1',
          fontSize: 10.5,
          lineHeight: 1.5,
        }}>
          {isLyran
            ? 'This agent uses the same existing workspace configuration and runtime behaviour.'
            : 'All existing agent configuration and skill behaviour is preserved.'}
        </div>
      </div>
    </div>

    {browseModalOpen && (
      <div
        onClick={() => setBrowseModalOpen(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(28,28,26,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: 'var(--lyra-color-surface)', borderRadius: 16, width: '100%', maxWidth: 720,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,.25)', overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--app-surface-3)', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: WS.ink }}>Browse skill library</div>
              <div style={{ fontSize: 11.5, color: WS.ink3, marginTop: 2 }}>Click a skill to add it — click an active one again to remove it.</div>
            </div>
            <button
              type="button"
              onClick={() => setBrowseModalOpen(false)}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: WS.ink3, padding: 4 }}
            >
              <X size={18} />
            </button>
          </div>

          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--app-surface-3)', flexShrink: 0 }}>
            <div className="awc-search-box" style={{ height: 38 }}>
              <Sparkles size={14} />
              <input
                autoFocus
                value={modalSearch}
                onChange={e => setModalSearch(e.target.value)}
                placeholder="Search all skills by name, description or tag..."
                style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', font: 'inherit', fontSize: 12.5, color: WS.ink }}
              />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 20px' }}>
            {groupedModalResults.length === 0 ? (
              <div style={{ textAlign: 'center', color: WS.ink4, fontSize: 12.5, padding: '40px 0' }}>
                No skills match "{modalSearch}".
              </div>
            ) : (
              groupedModalResults.map(({ domain, skills }) => (
                <div key={domain.id} style={{ marginBottom: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 13 }}>{domain.emoji}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: WS.ink2 }}>{domain.label}</span>
                    <span style={{ fontSize: 10.5, color: WS.ink4 }}>({skills.length})</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                    {skills.map(skill => (
                      <SkillCard
                        key={skill.id}
                        skill={skill}
                        isActive={activeSkillIds.includes(skill.id)}
                        onToggle={onToggleSkill}
                        compact
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
